/**
 * Admin Panel Module
 *
 * Super Admin only interface for managing:
 * - Organizations
 * - Organization Members
 * - Vessels across organizations
 *
 * Access restricted to super_admin role only.
 */

import React, { useState } from 'react';
import {
  Shield,
  Building2,
  AlertTriangle,
  Eye
} from 'lucide-react';

import { usePermissions } from '../../../hooks/usePermissions';
import OrganizationsTab from './OrganizationsTab';
import { AdminImpersonationPanel } from './AdminImpersonationPanel';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('organizations');
  const { isSuperAdmin, currentUser } = usePermissions();

  // Tabs configuration
  const tabs = [
    {
      id: 'organizations',
      label: 'Organizations',
      icon: Building2,
      badge: null
    },
    {
      id: 'impersonation',
      label: 'Impersonation',
      icon: Eye,
      badge: null
    }
  ];

  // Access check - CRITICAL
  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Access Denied
          </h2>
          <p className="text-red-700">
            The Admin Panel is only available to Super Administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Admin Panel
            </h1>
            <p className="text-slate-300 text-sm mt-1">
              {currentUser?.name || 'Super Admin'} - System Administration
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium
                border-b-2 transition-colors relative
                ${activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-cyan-600 text-white rounded">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto bg-slate-50 p-6">
        {activeTab === 'organizations' && <OrganizationsTab />}
        {activeTab === 'impersonation' && <AdminImpersonationPanel />}
      </div>
    </div>
  );
};

export default AdminPanel;
